import { createCoalescedRefresh } from '../platform/coalescedRefresh';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Spin } from 'antd';
import { ApiError, apiRequest, jsonBody } from '../platform/api';
import { Login } from '../pages/Login';
import { ChangeInitialPassword } from '../pages/ChangeInitialPassword';
import type { CloudUserRole } from './roles';
import { useTryOnStore } from '../stores/useTryOnStore';
import {
  activateResourceCache,
  clearAllResourceCaches,
  prepareResourceCacheAccount,
  subscribeResourceManifestDiff,
} from '../platform/resourceCache';
import { useModelStore } from '../stores/useModelStore';
import { usePromptStore } from '../stores/usePromptStore';
import { useResultStore } from '../stores/useResultStore';
import { useSettingsStore } from '../stores/useSettingsStore';
import { useTaskStore } from '../stores/useTaskStore';
import { advanceSessionEpoch, captureSessionEpoch, isSessionEpochCurrent } from '../platform/sessionEpoch';
import { closeWebBridgeSession, startWebBridgeSession } from '../platform/webBridge';
import {
  authorizationFingerprint,
  authorizationTransition,
  AuthTransitionGate,
  completeLogoutBoundary,
  reconcileSelectedModels,
  resourceAuthorizationFingerprint,
  retainSelectedModelsAfterManifest,
  verifyLogoutRecovery,
  type AuthenticationAcceptSource,
} from './sessionRuntime';
import { resetAllWorkspaceStores, resetRevokedWorkspaceStores } from './workspaceStoreReset';

export type FeatureKey = 'tryon' | 'fabric-tryon' | 'fabric-display' | 'garment-display';

export interface CloudUser {
  id: string;
  username: string;
  role: CloudUserRole;
  points: number;
  mustChangePassword: boolean;
  featureKeys: FeatureKey[];
  authorizationRevision: string;
}

interface AuthValue {
  user: CloudUser | null;
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue>({ user: null, refresh: async () => false, logout: async () => undefined });

function resetAccountState(): void {
  resetAllWorkspaceStores();
  useModelStore.getState().reset();
  usePromptStore.getState().reset();
  useResultStore.getState().reset();
  useSettingsStore.getState().reset();
  useTaskStore.getState().reset();
}

function reloadAccountState(): void {
  void Promise.all([
    useModelStore.getState().loadModels(),
    usePromptStore.getState().loadPrompts(),
    useResultStore.getState().loadResults(),
    useSettingsStore.getState().loadSettings(),
    useTaskStore.getState().loadTasks(),
  ]);
}

async function reloadModelCatalogAndReconcileSelection(resetBeforeLoad: boolean): Promise<void> {
  const epoch = captureSessionEpoch();
  if (resetBeforeLoad) useModelStore.getState().reset();
  const loaded = await useModelStore.getState().loadModels();
  if (!loaded || !isSessionEpochCurrent(epoch)) return;
  useTryOnStore.getState().setSelectedModels((selected) =>
    reconcileSelectedModels(selected, useModelStore.getState().models));
}

function reloadResourceCatalogs(): void {
  usePromptStore.getState().reset();
  void usePromptStore.getState().loadPrompts();
  void reloadModelCatalogAndReconcileSelection(true);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CloudUser | null>(null);
  const [loading, setLoading] = useState(Boolean(window.CLOUD_RUNTIME));
  const activeUserIdRef = useRef<string | null>(null);
  const activeBoundaryRef = useRef<string | null>(null);
  const activeResourceAuthorizationRef = useRef<string | null>(null);
  const activeFeatureKeysRef = useRef<FeatureKey[] | null>(null);
  const authGenerationRef = useRef(0);
  const transitionGateRef = useRef<AuthTransitionGate>();
  if (!transitionGateRef.current) transitionGateRef.current = new AuthTransitionGate();

  const acceptAuthenticatedUser = useCallback((
    nextUser: CloudUser,
    source: AuthenticationAcceptSource = 'normal',
  ): boolean => {
    if (!transitionGateRef.current!.canAccept(source)) return false;
    const transition = authorizationTransition(
      activeBoundaryRef.current,
      activeResourceAuthorizationRef.current,
      nextUser,
    );
    const authorizationChanged = transition.boundaryChanged || transition.resourcesChanged;
    if (authorizationChanged) {
      ++authGenerationRef.current;
      advanceSessionEpoch();
    }
    if (transition.boundaryChanged) {
      closeWebBridgeSession();
      resetAccountState();
    } else if (transition.resourcesChanged) {
      resetRevokedWorkspaceStores(activeFeatureKeysRef.current || [], nextUser.featureKeys);
      reloadResourceCatalogs();
    }
    prepareResourceCacheAccount(nextUser.id, { force: authorizationChanged });
    activeUserIdRef.current = nextUser.id;
    activeBoundaryRef.current = authorizationFingerprint(nextUser);
    activeResourceAuthorizationRef.current = resourceAuthorizationFingerprint(nextUser);
    activeFeatureKeysRef.current = [...nextUser.featureKeys];
    setUser(nextUser);
    setLoading(false);
    if (!nextUser.mustChangePassword) startWebBridgeSession(nextUser.id);
    if (transition.boundaryChanged) reloadAccountState();
    void activateResourceCache(nextUser.id, { force: authorizationChanged });
    return true;
  }, []);

  const expireAuthentication = useCallback(() => {
    if (transitionGateRef.current!.isLogoutActive()) return;
    ++authGenerationRef.current;
    advanceSessionEpoch();
    closeWebBridgeSession();
    resetAccountState();
    activeUserIdRef.current = null;
    activeBoundaryRef.current = null;
    activeResourceAuthorizationRef.current = null;
    activeFeatureKeysRef.current = null;
    void clearAllResourceCaches();
    setUser(null);
    setLoading(false);
  }, []);

  const refresh = useCallback(async () => {
    if (!window.CLOUD_RUNTIME || !transitionGateRef.current!.canRefresh()) return false;
    const requestGeneration = authGenerationRef.current;
    try {
      const response = await apiRequest<{ user: CloudUser }>('/api/auth/me');
      if (requestGeneration !== authGenerationRef.current || !transitionGateRef.current!.canRefresh()) return false;
      return acceptAuthenticatedUser(response.user);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && requestGeneration === authGenerationRef.current) {
        expireAuthentication();
      }
      return false;
    } finally {
      if (requestGeneration === authGenerationRef.current && transitionGateRef.current!.canRefresh()) setLoading(false);
    }
  }, [acceptAuthenticatedUser, expireAuthentication]);

  useEffect(() => {
    const accountRefresh = createCoalescedRefresh(refresh);
    const refreshListener = () => accountRefresh.request();
    const expiredListener = () => expireAuthentication();
    const unsubscribeManifest = subscribeResourceManifestDiff(['model-thumbnail'], (diff) => {
      if (!activeUserIdRef.current) return;
      useTryOnStore.getState().setSelectedModels((selected) =>
        retainSelectedModelsAfterManifest(selected, diff));
      const revoked = diff.revoked.some((entry) => entry.kind === 'model-thumbnail');
      void reloadModelCatalogAndReconcileSelection(revoked);
    });
    window.addEventListener('cloud-account-refresh', refreshListener);
    window.addEventListener('cloud-auth-expired', expiredListener);
    void refresh();
    return () => {
      accountRefresh.dispose();
      window.removeEventListener('cloud-account-refresh', refreshListener);
      window.removeEventListener('cloud-auth-expired', expiredListener);
      unsubscribeManifest();
    };
  }, [expireAuthentication, refresh]);

  useEffect(() => {
    if (!window.CLOUD_RUNTIME || !user || user.mustChangePassword) return;
    const heartbeat = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const timer = window.setInterval(heartbeat, 60_000);
    document.addEventListener('visibilitychange', heartbeat);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', heartbeat);
    };
  }, [refresh, user?.id, user?.mustChangePassword]);

  const value = useMemo<AuthValue>(() => ({
    user,
    refresh,
    logout: async () => {
      if (!transitionGateRef.current!.beginLogout()) return;
      const previousBoundary = activeBoundaryRef.current;
      const previousResourceAuthorization = activeResourceAuthorizationRef.current;
      const previousFeatureKeys = activeFeatureKeysRef.current;
      let recoveredAuthenticatedUser = false;
      let recoveredLoggedOut = false;
      let recoveryUnknown: unknown;
      ++authGenerationRef.current;
      advanceSessionEpoch();
      closeWebBridgeSession();
      activeUserIdRef.current = null;
      activeBoundaryRef.current = null;
      activeResourceAuthorizationRef.current = null;
      activeFeatureKeysRef.current = null;
      setLoading(true);
      const cacheCleanup = clearAllResourceCaches();
      try {
        await completeLogoutBoundary(
          () => apiRequest('/api/auth/logout', {
            method: 'POST',
            ...jsonBody({}),
            suppressAuthExpiredEvent: true,
          }),
          cacheCleanup,
          () => {
            resetAccountState();
            transitionGateRef.current!.finishLogout();
            setUser(null);
            setLoading(false);
          },
          async () => {
            const recovery = await verifyLogoutRecovery(
              async () => (await apiRequest<{ user: CloudUser }>('/api/auth/me', {
                suppressAuthExpiredEvent: true,
              })).user,
              (error) => error instanceof ApiError && error.status === 401,
            );
            if (recovery.kind === 'authenticated') {
              activeBoundaryRef.current = previousBoundary;
              activeResourceAuthorizationRef.current = previousResourceAuthorization;
              activeFeatureKeysRef.current = previousFeatureKeys;
              acceptAuthenticatedUser(recovery.user, 'logout-recovery');
              transitionGateRef.current!.finishLogout();
              recoveredAuthenticatedUser = true;
              return;
            }
            if (recovery.kind === 'logged-out') {
              resetAccountState();
              activeUserIdRef.current = null;
              activeBoundaryRef.current = null;
              activeResourceAuthorizationRef.current = null;
              activeFeatureKeysRef.current = null;
              transitionGateRef.current!.finishLogout();
              setUser(null);
              setLoading(false);
              recoveredLoggedOut = true;
              return;
            }
            recoveryUnknown = recovery.error;
            setLoading(true);
          },
        );
      } catch (error) {
        if (recoveredLoggedOut) return;
        const failure = new Error(recoveredAuthenticatedUser
          ? '退出未完成，已恢复当前有效登录账号，请重试'
          : '无法确认当前登录状态，请刷新页面后重试');
        (failure as Error & { cause?: unknown }).cause = recoveryUnknown || error;
        throw failure;
      }
    },
  }), [acceptAuthenticatedUser, refresh, user]);

  if (!window.CLOUD_RUNTIME) return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  if (loading) return <div className="auth-loading"><Spin size="large" /></div>;
  if (!user) return <Login onSuccess={(next) => {
    acceptAuthenticatedUser(next);
  }} />;
  if (user.mustChangePassword) return <ChangeInitialPassword user={user} onSuccess={async () => { await refresh(); }} />;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}
